'use client'
import { Box, Typography } from '@mui/material'
import Link from 'next/link'
import KeyValueStripedList from '../../../src/components/UI/Lists/KeyValueStripedList'
import { firstPartyCookies, thirdPartyCookies } from './data'
import CarBackdrop from '@/components/CarBackdrop/CarBackdrop'

const PageContent = () => {
  return (
    <>
      <Box component="section" mb={6}>
        <Typography variant="h1" sx={{ fontSize: { xs: '32px', sm: '50px' } }}>
          Cookies
        </Typography>
        <Typography variant="body1">
          Zuto&apos;s website (our website) uses cookies to distinguish you from other users of our
          website. This helps us to provide you with a good experience when you browse our website
          and also allows us to improve our website. This policy explains what we mean by
          &quot;cookies&quot;, the cookies we use on our website, what the cookies do, how long they
          last for and where you can find out more information about them.
        </Typography>
      </Box>

      <Box component="section" mb={6}>
        <Typography variant="h2">What are cookies?</Typography>
        <Typography variant="body1">
          Cookies are pieces of data created when you visit a website, and contain a unique,
          identification number. Some cookies are stored in the cookie directory of your hard drive,
          and do not expire at the end of your session. Cookies do not contain any personal
          information about you and cannot be used to identify an individual user.
        </Typography>
        <Typography variant="body1">
          Cookies have a lifetime. Many only last until you close your browser or for a short time.
          These are called &quot;session cookies&quot;. Some last far longer though, for example, to
          enable a website to recognise you when you return to them. These are called
          &quot;persistent cookies&quot;. Where possible, we&apos;ll let you know how long these
          cookies last for.
        </Typography>
        <Typography variant="body1">
          Cookies set directly by a website owner (like us) are generally known as &quot;first party
          cookies&quot; Many (if not most) modern websites also use bits of hidden technology
          provided by other parties. Online advertising is a particularly common example. These
          other parties may also set cookies when you use our website. If they do so, their cookies
          are generally known as &quot;third party cookies&quot;.
        </Typography>
        <Typography variant="body1">
          There are some other computer technologies which are technically different to cookies but
          are used to perform very similar jobs and function in a very similar way. For example,
          &quot;locally stored objects&quot; (or &quot;flash cookies&quot;) are used to power some
          online video content, and &quot;web bugs&quot; (or &quot;web beacons&quot;) are used for
          many of the same purposes as cookies. When we provide services to you through our website,
          we want to make them easy to use, useful and reliable. Cookies are used to improve
          services for you through, for example:
        </Typography>
        <ul>
          <li>
            enabling a service to recognise your device so you donapos;t have to give the same
            information several times during one task;
          </li>
          <li>
            recognising that you may already have given a username and password so you donapos;t
            need to do it for every web page requested; and
          </li>
          <li>
            measuring how many people are using services, so they can be made easier to use and
            there is enough capacity to ensure they are fast.
          </li>
        </ul>
        <Typography variant="body1">
          Now that we have explained what cookies are, please read &quot;Our use of cookies&quot; to
          understand why we use them.
        </Typography>
      </Box>

      <Box component="section" mb={6}>
        <Typography variant="h2">Our use of cookies</Typography>
        <Typography variant="body1">
          We have set out brief details of the cookies we use below, together with an overview of
          why we use them:
        </Typography>
        <Typography variant="body1">
          <strong>First party cookies</strong> - These are cookies we set.
        </Typography>
        <KeyValueStripedList data={firstPartyCookies} fontVariant="body2" />
        <Typography variant="body1">
          We regularly review the cookies we use and which our suppliers use to keep them to the
          minimum we think we need. In doing so, we aim to respect your right to privacy in your
          online life, which is very important to us, but also ensure you have a good browsing
          experience and ensure we have enough of the right kind of information to improve our
          website and the services which we offer.
        </Typography>

        <Typography variant="body1">
          <strong>Third party cookies</strong> - These are cookies which are set by other people.
          Please note that third parties (including, for example, advertising networks and providers
          of external services like web traffic analysis services) may also use cookies, over which
          we have no control. These cookies are likely to be analytical/performance cookies or
          targeting cookies.
        </Typography>
        <Typography variant="body1">We use the following third party cookies:</Typography>
        <KeyValueStripedList data={thirdPartyCookies} fontVariant="body2" />
        <Typography variant="body1">
          During your visit to our website you may notice some cookies that are not related to our
          website. This happens when you visit a page with content embedded from a third party or
          use some of the links to social networking websites (e.g. FaceBook). We love it when you
          share information through a social media network but when you do so, please be aware that,
          these websites may place cookies on your device. We do not control how a third party uses
          their cookies. You should check these third party websites&apos; privacy policies for more
          information about their cookies if you are concerned about this.
        </Typography>
      </Box>

      <Box component="section" mb={6}>
        <Typography variant="h2">Google Analytics Demographics and Interest Reporting</Typography>
        <Typography variant="body1">
          Zuto uses Google Analytics&apos;s 1st party cookies with DoubleClick&apos;s 3rd party
          cookies together to provide additional information on demographics and interests in our
          Google Analytics reporting.
        </Typography>
        <Typography variant="body1">
          We also uses Google Analytics features based on Display Advertising (Google Analytics
          Demographics and Interest Reporting). To opt out of Google Analytics for Display
          Advertising and customise Google Display Network ads using the Ads settings please see our{' '}
          <Link href="https://www.zuto.com/privacy" role="link">
            Privacy Policy
          </Link>
          .
        </Typography>
      </Box>

      <Box component="section" mb={6}>
        <Typography variant="h2">How can you manage cookies?</Typography>
        <Typography variant="body1">
          In case you do not agree with our Cookie Policy, we have provided the following details of
          how you can manage cookies yourself. By following the instructions below, you can tailor
          how your web browser behaves and to ensure only cookies and similar technologies with
          which you are happy are loaded on to your device.
        </Typography>
        <Typography variant="body1">
          Although your browser may be set up to allow the creation of cookies, you can specify that
          you be prompted before a website puts a cookie on your hard disk, so that you can decide
          whether to allow or disallow the cookie. Alternatively, you can set your computer not to
          accept any cookie. If you choose not to accept the essential cookies listed in the table
          above our website will not work properly. If you choose not to accept the non-essential
          cookies listed in the table above, you will not be able to access certain features of our
          website, your browsing experience will change and become less interactive and less
          personal to you. Our website is designed to use cookies, so it may be affected by some or
          all of these issues, as well as others not listed here.
        </Typography>
        <Typography variant="body1">
          Unless you have adjusted your browser setting so that it will refuse cookies, our system
          will issue cookies when you visit our website.
        </Typography>
      </Box>

      <Box component="section" mb={6}>
        <Typography variant="h2">Updates to this Cookie Policy</Typography>
        <Typography variant="body1">
          We review our use of cookies and similar technologies regularly. In doing so, we may
          change the cookies and other technologies we use, and how we use them. As a result, we
          will need to change this Cookie Policy from time to time to keep it accurate and up to
          date.
        </Typography>
        <Typography variant="body1">
          If we change this Cookie Policy, we will tell you when you next visit our website, and
          give you a link to our revised policy. That way you can check to see if you are happy with
          our new arrangements and adjust your browser settings if you want to, before proceeding
          any further.
        </Typography>
        <Typography variant="body1">
          Please look out for these notices in future when you are using our website.
        </Typography>
      </Box>
      <CarBackdrop />
    </>
  )
}

export default PageContent
